import React from 'react';
import { ExternalLink, Facebook, Instagram, Youtube, Twitter, Linkedin, Clock } from 'lucide-react';

// Configuración de plataformas de redes sociales
const SOCIAL_PLATFORMS = {
  facebook: { 
    name: 'Facebook', 
    icon: Facebook, 
    color: 'text-blue-600', 
    bg: 'bg-blue-50 hover:bg-blue-100',
    border: 'border-blue-200'
  },
  instagram: { 
    name: 'Instagram', 
    icon: Instagram, 
    color: 'text-pink-600', 
    bg: 'bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100',
    border: 'border-pink-200'
  },
  tiktok: { 
    name: 'TikTok', 
    icon: Clock, // TikTok icon placeholder
    color: 'text-gray-900', 
    bg: 'bg-gray-50 hover:bg-gray-100',
    border: 'border-gray-300'
  },
  youtube: { 
    name: 'YouTube', 
    icon: Youtube, 
    color: 'text-red-600', 
    bg: 'bg-red-50 hover:bg-red-100',
    border: 'border-red-200'
  },
  twitter: { 
    name: 'Twitter/X', 
    icon: Twitter, 
    color: 'text-sky-500', 
    bg: 'bg-sky-50 hover:bg-sky-100',
    border: 'border-sky-200'
  },
  linkedin: { 
    name: 'LinkedIn', 
    icon: Linkedin, 
    color: 'text-blue-700', 
    bg: 'bg-blue-50 hover:bg-blue-100',
    border: 'border-blue-200'
  },
};

/**
 * SocialMediaLinks - Muestra los enlaces a redes sociales del negocio
 * Solo muestra las redes que tienen URL configurada
 */
export function SocialMediaLinks({ socialMedia = [], compact = false }) {
  // Filtrar solo las redes que tienen URL
  const activeSocials = socialMedia.filter(sm => sm.profile_url && sm.profile_url.trim());

  if (activeSocials.length === 0) {
    return null;
  }

  if (compact) {
    // Vista compacta - solo iconos
    return (
      <div className="flex items-center gap-2" data-testid="social-media-links-compact">
        {activeSocials.map(social => {
          const platform = SOCIAL_PLATFORMS[social.platform];
          if (!platform) return null;
          const Icon = platform.icon;

          return (
            <a
              key={social.platform}
              href={social.profile_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`p-2 rounded-lg ${platform.bg} ${platform.color} transition-all hover:scale-110`}
              title={`Ver ${platform.name}`}
              data-testid={`social-link-${social.platform}`}
            >
              <Icon className="w-5 h-5" />
            </a>
          );
        })}
      </div>
    );
  }

  // Vista completa - con nombres y enlaces
  return (
    <div className="space-y-2" data-testid="social-media-links">
      <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
        <ExternalLink className="w-4 h-4" />
        Mis Redes Sociales
      </h4>
      <div className="flex flex-wrap gap-2">
        {activeSocials.map(social => {
          const platform = SOCIAL_PLATFORMS[social.platform];
          if (!platform) return null;
          const Icon = platform.icon;

          return (
            <a
              key={social.platform}
              href={social.profile_url}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${platform.border} ${platform.bg} ${platform.color} transition-all hover:shadow-md`}
              data-testid={`social-link-${social.platform}`}
            >
              <Icon className="w-4 h-4" />
              <span className="text-sm font-medium">{platform.name}</span>
              <ExternalLink className="w-3 h-3 opacity-50" />
            </a>
          );
        })}
      </div>
    </div>
  );
}
